// Business Intelligence & ML Service Layer
// Demand forecasting, anomaly detection, pricing signals, production planning
import { getPosSales, getAllPosProducts, getProductCostCard, getInventoryItems, getWastageLog } from './posStore'

// ────────────────────────────────────────────
// Demand Forecasting (Simple Moving Average + Day-of-Week)
// Uses rule-based fallback when data is sparse (<14 days)
// ────────────────────────────────────────────

export function forecastDemand(departmentId, targetDate) {
  // Get 30 days of historical sales
  const end = new Date(targetDate)
  const start = new Date(end)
  start.setDate(start.getDate() - 30)
  const startStr = start.toISOString().split('T')[0]
  const endStr = new Date(end.getTime() - 86400000).toISOString().split('T')[0] // exclude target day

  const sales = getPosSales(departmentId, startStr, endStr).filter(s => !s.voided)
  const products = getAllPosProducts(departmentId)

  // Group sales by date and product
  const dailySales = {} // { date: { productId: qty } }
  for (const sale of sales) {
    if (!dailySales[sale.date]) dailySales[sale.date] = {}
    for (const item of (sale.items || [])) {
      dailySales[sale.date][item.productId] = (dailySales[sale.date][item.productId] || 0) + (item.qty || 1)
    }
  }

  const dates = Object.keys(dailySales).sort()
  const targetDow = new Date(targetDate).getDay() // 0=Sun, 1=Mon...

  // If sparse data (<7 days), use simple average
  const useSparseMode = dates.length < 7

  const forecasts = []
  for (const product of products) {
    if (!product.isActive) continue

    // Get daily quantities for this product
    const dailyQty = dates.map(d => dailySales[d]?.[product.id] || 0)

    // Same day-of-week quantities
    const dowQty = dates
      .filter(d => new Date(d).getDay() === targetDow)
      .map(d => dailySales[d]?.[product.id] || 0)

    let forecastQty
    let method

    if (useSparseMode || dailyQty.length === 0) {
      // Fallback: average of all days (or 0)
      forecastQty = dailyQty.length > 0
        ? dailyQty.reduce((s, q) => s + q, 0) / dailyQty.length
        : 0
      method = 'simple_average'
    } else if (dowQty.length >= 3) {
      // Use day-of-week weighted average (more recent days weighted higher)
      const weights = dowQty.map((_, i) => i + 1) // [1, 2, 3, ...]
      const totalWeight = weights.reduce((s, w) => s + w, 0)
      forecastQty = dowQty.reduce((s, q, i) => s + q * weights[i], 0) / totalWeight
      method = 'dow_weighted'
    } else {
      // 7-day moving average
      const recent7 = dailyQty.slice(-7)
      forecastQty = recent7.reduce((s, q) => s + q, 0) / recent7.length
      method = 'moving_avg_7'
    }

    // Apply payday boost (1st-5th, 25th-31st of month)
    const day = new Date(targetDate).getDate()
    const isPayday = day <= 5 || day >= 25
    if (isPayday) forecastQty *= 1.2

    // Weekend reduction
    if (targetDow === 0) forecastQty *= 0.6 // Sunday
    if (targetDow === 6) forecastQty *= 0.8 // Saturday

    const costCard = getProductCostCard(product.id)

    forecasts.push({
      productId: product.id,
      productName: product.name,
      code: product.code,
      department: product.department,
      forecastQty: Math.round(forecastQty),
      avgDailyQty: dailyQty.length > 0 ? Number((dailyQty.reduce((s, q) => s + q, 0) / dailyQty.length).toFixed(1)) : 0,
      forecastRevenue: Number((Math.round(forecastQty) * (product.sellingPrice || 0)).toFixed(2)),
      forecastCOGS: costCard ? Number((Math.round(forecastQty) * costCard.cogs).toFixed(2)) : 0,
      confidence: useSparseMode ? 'low' : dowQty.length >= 3 ? 'high' : 'medium',
      method,
      daysOfData: dates.length,
    })
  }

  const totalForecastRevenue = forecasts.reduce((s, f) => s + f.forecastRevenue, 0)
  const totalForecastCOGS = forecasts.reduce((s, f) => s + f.forecastCOGS, 0)

  return {
    targetDate,
    departmentId,
    dayOfWeek: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][targetDow],
    isPayday: new Date(targetDate).getDate() <= 5 || new Date(targetDate).getDate() >= 25,
    products: forecasts.filter(f => f.forecastQty > 0).sort((a, b) => b.forecastRevenue - a.forecastRevenue),
    totalForecastRevenue: Number(totalForecastRevenue.toFixed(2)),
    totalForecastCOGS: Number(totalForecastCOGS.toFixed(2)),
    forecastProfit: Number((totalForecastRevenue - totalForecastCOGS).toFixed(2)),
    dataQuality: forecasts[0]?.daysOfData >= 21 ? 'good' : forecasts[0]?.daysOfData >= 7 ? 'fair' : 'sparse',
  }
}

// ────────────────────────────────────────────
// Anomaly Detection
// ────────────────────────────────────────────

export function detectAnomalies(departmentId, date) {
  // Get 30 days of data for baseline
  const end = new Date(date)
  const start = new Date(end)
  start.setDate(start.getDate() - 30)
  const startStr = start.toISOString().split('T')[0]

  const allSales = getPosSales(departmentId, startStr, date).filter(s => !s.voided)

  // Group by date
  const dailyTotals = {}
  for (const sale of allSales) {
    dailyTotals[sale.date] = (dailyTotals[sale.date] || 0) + (sale.total || 0)
  }

  const totals = Object.values(dailyTotals)
  if (totals.length < 7) return { anomalies: [], message: 'Insufficient data for anomaly detection (need 7+ days)' }

  const mean = totals.reduce((s, v) => s + v, 0) / totals.length
  const stdDev = Math.sqrt(totals.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / totals.length)
  const todayTotal = dailyTotals[date] || 0

  const anomalies = []

  // Revenue anomaly (>2 std deviations from mean)
  const zScore = stdDev > 0 ? (todayTotal - mean) / stdDev : 0
  if (Math.abs(zScore) > 2) {
    anomalies.push({
      type: zScore > 0 ? 'REVENUE_SPIKE' : 'REVENUE_DROP',
      severity: Math.abs(zScore) > 3 ? 'CRITICAL' : 'HIGH',
      metric: 'daily_revenue',
      value: todayTotal,
      expected: Number(mean.toFixed(2)),
      deviation: Number(zScore.toFixed(2)),
      message: zScore > 0
        ? `Revenue R${todayTotal.toFixed(0)} is ${zScore.toFixed(1)} std deviations above normal (avg R${mean.toFixed(0)})`
        : `Revenue R${todayTotal.toFixed(0)} is ${Math.abs(zScore).toFixed(1)} std deviations below normal (avg R${mean.toFixed(0)})`,
    })
  }

  // Transaction count anomaly
  const dailyTxCounts = {}
  for (const sale of allSales) {
    dailyTxCounts[sale.date] = (dailyTxCounts[sale.date] || 0) + 1
  }
  const txCounts = Object.values(dailyTxCounts)
  const txMean = txCounts.reduce((s, v) => s + v, 0) / txCounts.length
  const txStdDev = Math.sqrt(txCounts.reduce((s, v) => s + Math.pow(v - txMean, 2), 0) / txCounts.length)
  const todayTx = dailyTxCounts[date] || 0
  const txZ = txStdDev > 0 ? (todayTx - txMean) / txStdDev : 0

  if (Math.abs(txZ) > 2) {
    anomalies.push({
      type: txZ > 0 ? 'TRANSACTION_SPIKE' : 'TRANSACTION_DROP',
      severity: 'MEDIUM',
      metric: 'transaction_count',
      value: todayTx,
      expected: Number(txMean.toFixed(0)),
      deviation: Number(txZ.toFixed(2)),
      message: `${todayTx} transactions vs avg ${txMean.toFixed(0)} (z-score: ${txZ.toFixed(1)})`,
    })
  }

  // Wastage anomaly
  const wastage = getWastageLog(departmentId, startStr, date)
  const dailyWaste = {}
  for (const w of wastage) {
    dailyWaste[w.date] = (dailyWaste[w.date] || 0) + (w.qty || 0)
  }
  const wasteVals = Object.values(dailyWaste)
  if (wasteVals.length >= 3) {
    const wasteMean = wasteVals.reduce((s, v) => s + v, 0) / wasteVals.length
    const wasteStd = Math.sqrt(wasteVals.reduce((s, v) => s + Math.pow(v - wasteMean, 2), 0) / wasteVals.length)
    const todayWaste = dailyWaste[date] || 0
    const wasteZ = wasteStd > 0 ? (todayWaste - wasteMean) / wasteStd : 0

    if (wasteZ > 2) {
      anomalies.push({
        type: 'WASTAGE_SPIKE',
        severity: 'HIGH',
        metric: 'daily_wastage',
        value: todayWaste,
        expected: Number(wasteMean.toFixed(1)),
        deviation: Number(wasteZ.toFixed(2)),
        message: `Wastage ${todayWaste.toFixed(1)} units is ${wasteZ.toFixed(1)}x above normal`,
      })
    }
  }

  return {
    date,
    departmentId,
    anomalies,
    baseline: { mean: Number(mean.toFixed(2)), stdDev: Number(stdDev.toFixed(2)), days: totals.length },
    todayRevenue: todayTotal,
    todayZScore: Number(zScore.toFixed(2)),
  }
}

// ────────────────────────────────────────────
// Pricing Optimization Signals
// ────────────────────────────────────────────

export function getPricingSignals(departmentId) {
  const products = getAllPosProducts(departmentId)
  const signals = []

  for (const product of products) {
    if (!product.isActive) continue
    const card = getProductCostCard(product.id)
    if (!card) continue

    // Underpriced: margin < 15%
    if (card.grossMargin < 15 && card.grossMargin >= 0) {
      const suggestedPrice = card.cogs > 0 ? Math.ceil(card.cogs / 0.65) : product.sellingPrice // target 35% margin
      signals.push({
        productId: product.id,
        productName: product.name,
        code: product.code,
        signal: 'UNDERPRICED',
        severity: card.grossMargin < 5 ? 'CRITICAL' : 'HIGH',
        currentPrice: product.sellingPrice,
        cogs: Number(card.cogs.toFixed(2)),
        currentMargin: Number(card.grossMargin.toFixed(1)),
        suggestedPrice,
        suggestedMargin: suggestedPrice > 0 ? Number(((1 - card.cogs / suggestedPrice) * 100).toFixed(1)) : 0,
        message: `${product.name} (R${product.sellingPrice}) has only ${card.grossMargin.toFixed(0)}% margin. Consider R${suggestedPrice}.`,
      })
    }

    // Loss-making
    if (card.grossMargin < 0) {
      const minPrice = Math.ceil(card.cogs * 1.3) // 30% minimum margin
      signals.push({
        productId: product.id,
        productName: product.name,
        code: product.code,
        signal: 'LOSS_MAKER',
        severity: 'CRITICAL',
        currentPrice: product.sellingPrice,
        cogs: Number(card.cogs.toFixed(2)),
        currentMargin: Number(card.grossMargin.toFixed(1)),
        suggestedPrice: minPrice,
        message: `${product.name} sells at R${product.sellingPrice} but costs R${card.cogs.toFixed(2)} to make. Losing money!`,
      })
    }

    // High margin opportunity (>60% margin, could be lowered to drive volume)
    if (card.grossMargin > 60) {
      signals.push({
        productId: product.id,
        productName: product.name,
        code: product.code,
        signal: 'HIGH_MARGIN',
        severity: 'INFO',
        currentPrice: product.sellingPrice,
        cogs: Number(card.cogs.toFixed(2)),
        currentMargin: Number(card.grossMargin.toFixed(1)),
        message: `${product.name} has ${card.grossMargin.toFixed(0)}% margin — potential to lower price to drive volume.`,
      })
    }
  }

  return signals.sort((a, b) => {
    const sev = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, INFO: 3 }
    return (sev[a.severity] || 4) - (sev[b.severity] || 4)
  })
}

// ────────────────────────────────────────────
// Production Planning (what to make today)
// ────────────────────────────────────────────

export function getProductionPlan(departmentId, date) {
  const forecast = forecastDemand(departmentId, date)
  const inventory = getInventoryItems(departmentId)

  const plan = []
  for (const item of forecast.products) {
    if (item.forecastQty <= 0) continue

    // Check if we have enough ingredients
    const product = getAllPosProducts(departmentId).find(p => p.id === item.productId)
    if (!product || product.type === 'resale') continue

    plan.push({
      productId: item.productId,
      productName: item.productName,
      code: item.code,
      forecastQty: item.forecastQty,
      forecastRevenue: item.forecastRevenue,
      confidence: item.confidence,
      priority: item.forecastRevenue > 100 ? 'high' : item.forecastRevenue > 30 ? 'medium' : 'low',
    })
  }

  // Low stock alerts
  const lowStockAlerts = inventory
    .filter(i => i.currentStock <= i.reorderLevel && i.reorderLevel > 0)
    .map(i => ({
      itemId: i.id,
      name: i.name,
      currentStock: i.currentStock,
      reorderLevel: i.reorderLevel,
      unit: i.unit,
      urgent: i.currentStock === 0,
    }))

  return {
    date,
    departmentId,
    items: plan.sort((a, b) => b.forecastRevenue - a.forecastRevenue),
    totalForecastRevenue: forecast.totalForecastRevenue,
    lowStockAlerts,
    dataQuality: forecast.dataQuality,
  }
}

// ────────────────────────────────────────────
// Contribution Margin Report
// ────────────────────────────────────────────

export function getContributionMarginReport(departmentId, dateFrom, dateTo) {
  const sales = getPosSales(departmentId, dateFrom, dateTo).filter(s => !s.voided)
  const products = getAllPosProducts(departmentId)

  const productStats = {} // { productId: { revenue, cogs, qty, name, code } }

  for (const sale of sales) {
    for (const item of (sale.items || [])) {
      if (!productStats[item.productId]) {
        const product = products.find(p => p.id === item.productId)
        const card = product ? getProductCostCard(product.id) : null
        productStats[item.productId] = {
          productId: item.productId,
          name: product?.name || 'Unknown',
          code: product?.code || '?',
          department: product?.department || '',
          revenue: 0,
          cogs: 0,
          qty: 0,
          unitCOGS: card?.cogs || 0,
          sellingPrice: product?.sellingPrice || 0,
        }
      }
      const qty = item.qty || 1
      productStats[item.productId].qty += qty
      productStats[item.productId].revenue += (item.price || 0) * qty
      productStats[item.productId].cogs += productStats[item.productId].unitCOGS * qty
    }
  }

  const report = Object.values(productStats).map(p => ({
    ...p,
    contributionMargin: p.revenue - p.cogs,
    contributionPct: p.revenue > 0 ? Number(((p.revenue - p.cogs) / p.revenue * 100).toFixed(1)) : 0,
  })).sort((a, b) => b.contributionMargin - a.contributionMargin)

  const totalRevenue = report.reduce((s, p) => s + p.revenue, 0)
  const totalCOGS = report.reduce((s, p) => s + p.cogs, 0)

  return {
    period: { from: dateFrom, to: dateTo },
    products: report,
    totals: {
      revenue: Number(totalRevenue.toFixed(2)),
      cogs: Number(totalCOGS.toFixed(2)),
      contributionMargin: Number((totalRevenue - totalCOGS).toFixed(2)),
      contributionPct: totalRevenue > 0 ? Number(((totalRevenue - totalCOGS) / totalRevenue * 100).toFixed(1)) : 0,
    },
    topContributors: report.filter(p => p.contributionMargin > 0).slice(0, 5),
    marginEroders: report.filter(p => p.contributionMargin < 0),
  }
}
